import apiClient, { axiosClientWithoutToken } from './config';
import type { MemberContextPayload } from '../utils/memberContextPayload';

export type { MemberContextPayload } from '../utils/memberContextPayload';

// Types for API requests and responses
export interface Tag {
    id: number;
    name: string;
    name_fr?: string;
}

export interface PartnershipPartner {
    id: number;
    name: string;
    type: string;
    role_in_partnership: string;
    member_status: string;
    email?: string;
    members_count?: number;
    city?: string;
    zip_code?: string;
}

export interface PartnershipUserContext {
    first_name: string;
    last_name: string;
    organization_role: string | null;
    organization_name: string | null;
}

export interface Partnership {
    id: number;
    initiator_type?: string;
    initiator_id?: number;
    partnership_type: string;
    status: string;
    share_members: boolean;
    share_projects: boolean;
    partners: PartnershipPartner[];
    created_at: string;
    updated_at: string;
    name?: string;
    description?: string;
    has_sponsorship?: boolean;
    partnership_kind?: string | null;
    initiator_user?: PartnershipUserContext | null;
    confirmed_at?: string | null;
    confirmed_by_user?: PartnershipUserContext | null;
    rejected_at?: string | null;
    rejected_by_user?: PartnershipUserContext | null;
}

export interface OrganizationMember {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    email: string;
    role: string;
    avatar_url: string | null;
}

export interface ProjectMemberAttribute {
    user_id: number;
    role: 'member' | 'admin' | 'co_owner';
    status: 'pending' | 'confirmed';
    can_assign_badges_in_project?: boolean;
}

export interface LinkAttribute {
    id?: number;
    name: string;
    url: string;
}

/** Ligne HV — nom d’enseignant + heures HV (`hour`). */
export interface FinancialHvLine {
    teacher_name: string;
    hour: string;
}

/** Ligne HSE — intitulé + heures (`hour`) + commentaire optionnel. */
export interface FinancialHseLine {
    hse_name: string;
    hour: string;
    comment?: string | null;
}

export {
    getMldsAutresFinancementsFromMldsInfo,
    getMldsBilanHseLines,
    getMldsBilanHvLines,
    getMldsBilanRecord,
    getMldsHseLinesFromMldsInfo,
    getMldsHvLinesFromMldsInfo,
    getMldsOperatingLinesFromMldsInfo,
    getMldsTransportLinesFromMldsInfo,
    hasMldsBilanData,
    mergeMldsLineLabel,
    normalizeMldsLineCollection,
    sumMldsFinancialCreditsEuro,
    sumMldsHvCreditsEuro,
} from '../utils/mldsFinancialLines';

/** Prestataire : montant, heures d’intervention, devis optionnel (fichier via FormData `quote`) — lecture API héritée */
export interface FinancialServiceLine {
    service_name: string;
    price: string;
    hours?: string;
    quote_url?: string | null;
}

/** Nested attributes Rails — table `mlds_financial_service_lines` ; devis : `quote` en multipart */
export interface MldsFinancialServiceLineAttribute {
    id?: number;
    _destroy?: string | boolean;
    /** Requis pour création / mise à jour ; omis si uniquement `_destroy` (suppression Rails) */
    position?: number;
    service_name?: string;
    price?: string;
    hours?: string | null;
    comment?: string | null;
}

/** Ligne prestataire renvoyée par l’API (association ou ancien JSON inline). */
export type MldsServiceLineRead = {
    id?: number;
    position?: number;
    service_name?: string;
    price?: string | number;
    hours?: string | number;
    comment?: string | null;
    quote_url?: string | null;
};

export function getMldsServiceLinesFromMldsInfo(mlds: unknown): MldsServiceLineRead[] {
    const m = mlds as Record<string, unknown> | null | undefined;
    if (!m) return [];
    const fromTable = m.mlds_financial_service_lines;
    if (Array.isArray(fromTable) && fromTable.length > 0) {
        return fromTable as MldsServiceLineRead[];
    }
    const legacy = m.financial_service;
    if (Array.isArray(legacy) && legacy.length > 0) {
        return legacy as MldsServiceLineRead[];
    }
    if (legacy != null && typeof legacy !== 'object') {
        return [{ price: String(legacy) }];
    }
    return [];
}

export interface MLDSInformationAttributes {
    /** Requis pour la mise à jour Rails (`accepts_nested_attributes_for`) */
    id?: number;
    requested_by?: string;
    department_number?: string | null;
    /** Type de projet MLDS (ex: perseverance, remediation) */
    type_mlds?: string | null;
    school_level_ids?: number[];
    target_audience?: string;
    action_objectives?: string[];
    action_objectives_other?: string | null;
    competencies_developed?: string | null;
    expected_participants?: number | null;
    /** Ancien champ (montant HSE global) — préférer `financial_hse_lines` */
    financial_hse?: number | null;
    financial_hse_lines?: FinancialHseLine[] | null;
    /** Taux horaire (€/h) principal pour MLDS */
    financial_rate?: number | null;
    /** Ancien champ (heures HV globales) — préférer financial_hv_lines pour le détail par enseignant */
    financial_hv?: number | null;
    /** Crédits HV par enseignant (intégrés au total des crédits) */
    financial_hv_lines?: FinancialHvLine[] | null;
    financial_transport?: Array<{ transport_name: string; price: string }> | null;
    /** Frais de fonctionnement — inclus dans le total Crédits */
    financial_operating?: Array<{ operating_name: string; price: string }> | null;
    /** Autres financements — hors total Crédits ; s’ajoutent au total général */
    financial_autres_financements?: Array<{ autres_name: string; price: string }> | null;
    /** Prestataires — nested `mlds_financial_service_lines` ; fichiers `quote` via FormData */
    mlds_financial_service_lines_attributes?: MldsFinancialServiceLineAttribute[] | null;
    /** Ancien format JSON inline — encore possible en lecture API ; préférer `mlds_financial_service_lines` */
    financial_service?: FinancialServiceLine[] | null;
    objectives?: string | null;
    organization_names?: string[];
    network_issue_addressed?: string | null;
}

/** Payload pour POST /api/v1/projects/:id/mlds_bilan (clôture MLDS). */
export interface MldsBilanPayload {
    financial_hse_lines?: Array<{ hse_name: string; hour: string; comment?: string | null }> | null;
    financial_rate?: number | null;
    financial_rate_comment?: string | null;
    financial_transport_comment?: string | null;
    financial_operating_comment?: string | null;
    financial_service_comment?: string | null;
    expected_participants?: number | null;
    expected_participants_comment?: string | null;
    financial_transport?: Array<{ transport_name: string; price: string; comment?: string | null }> | null;
    financial_operating?: Array<{ operating_name: string; price: string; comment?: string | null }> | null;
    financial_service?: Array<{
        service_name: string;
        price: string;
        hours?: string;
        comment?: string | null;
    }> | null;
    financial_hv_lines?: Array<{ teacher_name: string; hour: string; comment?: string | null }> | null;
    financial_autres_financements?: Array<{ autres_name: string; price: string; comment?: string | null }> | null;
}

export interface CreateProjectPayload {
    context: 'company' | 'school' | 'teacher' | 'general';
    organization_id?: number;
    project: {
        title?: string; // Optional for MLDS projects (backend auto-generates)
        description: string;
        start_date: string;
        end_date: string;
        participants_number?: number;
        private: boolean;
        status: 'draft' | 'to_process' | 'pending_validation' | 'coming' | 'in_progress' | 'ended' | 'archived';
        school_level_ids?: number[];
        group_ids?: number[];
        skill_ids?: number[];
        tag_ids?: number[];
        company_ids?: number[];
        keyword_ids?: string[];
        partnership_ids?: number[];
        project_members_attributes?: ProjectMemberAttribute[];
        links_attributes?: LinkAttribute[];
        co_responsible_ids?: number[];
        co_responsible_contexts?: MemberContextPayload[];
        participant_ids?: number[];
        participant_contexts?: MemberContextPayload[];
        mlds_information_attributes?: MLDSInformationAttributes;
        project_kind?: 'standard' | 'stage' | 'formation';
        is_eu_mc_declared?: boolean;
        learning_outcomes?: string;
        participation_mode?: 'on_site' | 'online' | 'blended';
        workload_hours?: number | string;
        workload_ects?: number | string;
        project_eqf_level?: number;
        project_eqf_framework_type?: 'EQF' | 'QF_EHEA';
        assessment_type?: string;
        teaching_languages?: string[];
        entry_requirements?: string;
        validity_period_months?: number | null;
    };
}

export interface CreateProjectResponse {
    id: number;
    title: string;
    description: string;
    status: string;
    start_date: string;
    end_date: string;
    members_count: number;
    teams_count: number;
    links: Array<{
        id: number;
        name: string;
        url: string;
        created_at: string;
        updated_at: string;
    }>;
    project_members: Array<{
        id: number;
        status: string;
        role: string;
        user: OrganizationMember;
    }>;
    keywords: Array<{
        id: number;
        name: string;
    }>;
    owner: any;
    skills: any[];
    tags: any[];
    school_levels: any[];
}

export interface ProjectShareLinkResponse {
    token: string;
}

/**
 * Fetch available tags (pathways)
 */
export const getTags = async (): Promise<Tag[]> => {
    const response = await apiClient.get('/api/v1/tags');
    // Handle both { data: [...] } and [...] formats
    const tagsData = response.data?.data || response.data || [];
    // Ensure it's an array
    return Array.isArray(tagsData) ? tagsData : [];
};

/**
 * Fetch teacher's projects (owned + projects for managed classes)
 */
export const getTeacherProjects = async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    search?: string;
}): Promise<{ data: any[]; meta: any }> => {
    const response = await apiClient.get('/api/v1/teachers/projects', { params });
    return {
        data: response.data?.data || response.data || [],
        meta: response.data?.meta || {}
    };
};

/**
 * Fetch school partnerships for a teacher (read-only, confirmed only)
 * Use when teacher selects a school in project modal and "en partenariat"
 */
export const getTeacherSchoolPartnerships = async (
    schoolId: number,
    params?: { page?: number; per_page?: number; status?: string }
): Promise<{ data: Partnership[]; meta?: any }> => {
    const response = await apiClient.get(`/api/v1/teachers/schools/${schoolId}/partnerships`, {
        params: params || { status: 'confirmed' }
    });
    if (response.data?.data) {
        return { data: response.data.data, meta: response.data.meta };
    }
    return { data: Array.isArray(response.data) ? response.data : [], meta: undefined };
};

/**
 * Fetch school members for a teacher (staff and community, excluding students)
 * Use for co-responsibles when teacher selects a school in project modal
 */
export const getTeacherSchoolMembers = async (
    schoolId: number,
    params?: { per_page?: number; page?: number; search?: string; exclude_me?: boolean }
): Promise<{ data: OrganizationMember[]; meta?: any }> => {
    const response = await apiClient.get(`/api/v1/teachers/schools/${schoolId}/members`, { params });
    const data = response.data?.data || response.data || [];
    return { data: Array.isArray(data) ? data : [], meta: response.data?.meta };
};

/**
 * Generate (or fetch existing) share token for a project
 */
export const getOrCreateProjectShareLink = async (
    projectId: number
): Promise<ProjectShareLinkResponse> => {
    const response = await apiClient.post(`/api/v1/projects/${projectId}/share_link`);
    return response.data;
};

/**
 * Fetch partnerships for an organization
 */
export const getPartnerships = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    params?: { page?: number; per_page?: number; status?: string; incoming_only?: boolean }
): Promise<{ data: Partnership[]; meta?: any }> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/partnerships`
        : `/api/v1/companies/${organizationId}/partnerships`;

    const response = await apiClient.get(endpoint, {
        params: params || { status: 'confirmed' }
    });
    
    // Handle response structure: { data: [...], meta: {...} }
    if (response.data?.data) {
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    }
    
    // Fallback for direct array response
    return {
        data: Array.isArray(response.data) ? response.data : [],
        meta: undefined
    };
};

const PARTNERSHIPS_PAGE_SIZE = 100;

/**
 * Load all confirmed partnerships (paginated API) for project modals / pickers.
 */
export const fetchAllConfirmedPartnerships = async (
    organizationId: number,
    organizationType: 'school' | 'company'
): Promise<{ data: Partnership[]; meta?: { total_count: number } }> => {
    const all: Partnership[] = [];
    let page = 1;
    let totalPages = 1;

    do {
        const { data, meta } = await getPartnerships(organizationId, organizationType, {
            status: 'confirmed',
            page,
            per_page: PARTNERSHIPS_PAGE_SIZE
        });
        all.push(...(data || []));
        totalPages = meta?.total_pages ?? 1;
        page += 1;
    } while (page <= totalPages);

    return { data: all, meta: { total_count: all.length } };
};

/**
 * Load all confirmed school partnerships for teacher project context.
 */
export const fetchAllTeacherSchoolPartnerships = async (
    schoolId: number
): Promise<{ data: Partnership[]; meta?: { total_count: number } }> => {
    const all: Partnership[] = [];
    let page = 1;
    let totalPages = 1;

    do {
        const { data, meta } = await getTeacherSchoolPartnerships(schoolId, {
            status: 'confirmed',
            page,
            per_page: PARTNERSHIPS_PAGE_SIZE
        });
        all.push(...(data || []));
        totalPages = meta?.total_pages ?? 1;
        page += 1;
    } while (page <= totalPages);

    return { data: all, meta: { total_count: all.length } };
};

/**
 * Fetch personal user network (for teacher/user roles)
 */
export const getPersonalUserNetwork = async (
    params?: { page?: number; per_page?: number; skill_ids?: number[]; availability?: string[]; skill_name?: string }
): Promise<{ data: any[]; meta?: any }> => {
    const requestParams: any = { per_page: params?.per_page || 1000 };
    if (params?.page) requestParams.page = params.page;
    if (params?.skill_ids && params.skill_ids.length > 0) requestParams.skill_ids = params.skill_ids;
    if (params?.skill_name) requestParams.skill_name = params.skill_name;
    
    // Format availability array with brackets for Rails-style array parameters
    if (params?.availability && params.availability.length > 0) {
        // Use paramsSerializer to format arrays with brackets
        requestParams.availability = params.availability;
    }

    const response = await apiClient.get('/api/v1/users/me/network', {
        params: requestParams,
        paramsSerializer: (params) => {
            // Format arrays with brackets: availability[]=monday&availability[]=tuesday
            const searchParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        searchParams.append(`${key}[]`, item);
                    });
                } else if (value !== undefined && value !== null) {
                    searchParams.append(key, value.toString());
                }
            });
            return searchParams.toString();
        }
    });
    
    // Handle response structure: { data: [...], meta: {...} }
    if (response.data?.data) {
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    }
    
    // Fallback for direct array response
    return {
        data: Array.isArray(response.data) ? response.data : [],
        meta: undefined
    };
};

/**
 * Join a school (for personal users)
 */
export const joinSchool = async (schoolId: number): Promise<any> => {
    const response = await apiClient.post(`/api/v1/schools/${schoolId}/join`);
    return response.data;
};

/**
 * Join a company (for personal users)
 */
export const joinCompany = async (companyId: number): Promise<any> => {
    const response = await apiClient.post(`/api/v1/companies/${companyId}/join`);
    return response.data;
};

/**
 * Accept a partnership request
 */
export const acceptPartnership = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    partnershipId: number
): Promise<any> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/partnerships/${partnershipId}/confirm`
        : `/api/v1/companies/${organizationId}/partnerships/${partnershipId}/confirm`;

    const response = await apiClient.patch(endpoint);
    return response.data;
};

/**
 * Reject a partnership request
 */
export const rejectPartnership = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    partnershipId: number
): Promise<any> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/partnerships/${partnershipId}/reject`
        : `/api/v1/companies/${organizationId}/partnerships/${partnershipId}/reject`;

    const response = await apiClient.patch(endpoint);
    return response.data;
};

// --- Teacher partnership requests (teacher → school) ---

export interface TeacherPartnershipRequest {
    id: number;
    school_id: number;
    status: 'pending' | 'approved' | 'rejected';
    partner_school_ids: number[];
    partner_company_ids: number[];
    partnership_type: string;
    name: string;
    description?: string;
    share_projects: boolean;
    share_members: boolean;
    rejection_reason?: string | null;
    created_at: string;
    updated_at: string;
    school?: { id: number; name: string };
    teacher?: { id: number; full_name?: string };
}

export interface CreateTeacherPartnershipRequestPayload {
    school_id: number;
    partner_school_ids: number[];
    partner_company_ids: number[];
    partnership_type: string;
    name: string;
    description?: string;
    share_projects: boolean;
    share_members: boolean;
}

export const getTeacherPartnershipRequests = async (): Promise<TeacherPartnershipRequest[]> => {
    const response = await apiClient.get('/api/v1/teachers/partnership_requests');
    return response.data.data ?? response.data ?? [];
};

export const getTeacherPartnershipRequest = async (id: number): Promise<TeacherPartnershipRequest> => {
    const response = await apiClient.get(`/api/v1/teachers/partnership_requests/${id}`);
    return response.data.data ?? response.data;
};

export const createTeacherPartnershipRequest = async (
    payload: CreateTeacherPartnershipRequestPayload
): Promise<{ data: TeacherPartnershipRequest; message?: string }> => {
    const response = await apiClient.post('/api/v1/teachers/partnership_requests', payload);
    return response.data;
};

export const deleteTeacherPartnershipRequest = async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/teachers/partnership_requests/${id}`);
};

// --- School: teacher partnership requests (demandes reçues des enseignants) ---

export const getSchoolTeacherPartnershipRequests = async (
    schoolId: number
): Promise<TeacherPartnershipRequest[]> => {
    const response = await apiClient.get(`/api/v1/schools/${schoolId}/teacher_partnership_requests`);
    return response.data.data ?? response.data ?? [];
};

export const getSchoolTeacherPartnershipRequest = async (
    schoolId: number,
    id: number
): Promise<TeacherPartnershipRequest> => {
    const response = await apiClient.get(`/api/v1/schools/${schoolId}/teacher_partnership_requests/${id}`);
    return response.data.data ?? response.data;
};

export const approveSchoolTeacherPartnershipRequest = async (
    schoolId: number,
    id: number
): Promise<{ data?: TeacherPartnershipRequest; message?: string }> => {
    const response = await apiClient.patch(
        `/api/v1/schools/${schoolId}/teacher_partnership_requests/${id}/approve`
    );
    return response.data;
};

export const rejectSchoolTeacherPartnershipRequest = async (
    schoolId: number,
    id: number,
    body?: { rejection_reason?: string }
): Promise<void> => {
    await apiClient.patch(
        `/api/v1/schools/${schoolId}/teacher_partnership_requests/${id}/reject`,
        body ?? {}
    );
};

/**
 * Create a partnership request
 */
export interface CreatePartnershipPayload {
    partnership_type: string;
    name: string;
    description: string;
    partner_company_ids: number[];
    partner_school_ids: number[];
    share_members: boolean;
    share_projects: boolean;
    has_sponsorship: boolean;
    initiator_role: 'beneficiary' | 'sponsor';
    partner_role: 'beneficiary' | 'sponsor';
    partnership_kind?: string | null;
}

export interface CreatePartnershipResponse {
    message: string;
    data: Partnership;
}

export const createPartnership = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    payload: CreatePartnershipPayload
): Promise<CreatePartnershipResponse> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/partnerships`
        : `/api/v1/companies/${organizationId}/partnerships`;

    const response = await apiClient.post(endpoint, payload);
    return response.data;
};

/**
 * Fetch members for an organization
 */
export const getOrganizationMembers = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    includePending: boolean = false
): Promise<OrganizationMember[]> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/members`
        : `/api/v1/companies/${organizationId}/members`;

    const params: any = { 
        per_page: 1000  // Load all members (adjust if needed)
    };
    
    // Only filter by status if not including pending
    if (!includePending) {
        params.status = 'confirmed';
    }

    const response = await apiClient.get(endpoint, { params });
    
    // Extract data array from paginated response
    // Backend returns: { data: [...], meta: {...} }
    return response.data?.data || response.data || [];
};

/**
 * Fetch students for teacher
 */
export const getTeacherStudents = async (): Promise<OrganizationMember[]> => {
    const response = await apiClient.get('/api/v1/teachers/students', {
        params: { per_page: 1000 }
    });
    return response.data?.data || response.data || [];
};

/**
 * Fetch network members for an organization (company or school)
 * Returns all members from the organization's network including partners and branches
 */
export const getOrganizationNetwork = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    excludeMe: boolean = true
): Promise<any[]> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/network`
        : `/api/v1/companies/${organizationId}/network`;

    const params: any = { 
        per_page: 1000  // Load all network members
    };
    
    if (excludeMe) {
        params.exclude_me = true;
    }

    const response = await apiClient.get(endpoint, { params });
    
    // Extract data array from paginated response
    // Backend returns: { data: [...], meta: {...} }
    return response.data?.data || response.data || [];
};

/**
 * Fetch parent organization for a school
 */
export const getSchoolParent = async (
    schoolId: number
): Promise<{ data: any; meta?: any }> => {
    const endpoint = `/api/v1/schools/${schoolId}/parent`;
    const response = await apiClient.get(endpoint);
    
    // Handle response structure: { data: {...}, meta: {...} }
    if (response.data?.data) {
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    }
    
    // Fallback for direct object response
    return {
        data: response.data || null,
        meta: undefined
    };
};

/**
 * Fetch parent organization for a company
 */
export const getCompanyParent = async (
    companyId: number
): Promise<{ data: any; meta?: any }> => {
    const endpoint = `/api/v1/companies/${companyId}/parent`;
    const response = await apiClient.get(endpoint);
    
    // Handle response structure: { data: {...}, meta: {...} }
    if (response.data?.data) {
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    }
    
    // Fallback for direct object response
    return {
        data: response.data || null,
        meta: undefined
    };
};

/**
 * Fetch sub-organizations (branches) for an organization
 * If branches endpoint returns 400, try to get parent organization instead
 */
export const getSubOrganizations = async (
    organizationId: number,
    organizationType: 'school' | 'company'
): Promise<{ data: any[]; meta?: any; isParent?: boolean }> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/branches`
        : `/api/v1/companies/${organizationId}/branches`;

    try {
        const response = await apiClient.get(endpoint);
        
        // Handle response structure: { data: [...], meta: {...} }
        if (response.data?.data) {
            return {
                data: response.data.data,
                meta: response.data.meta,
                isParent: false // Data comes from branches endpoint
            };
        }
        
        // Fallback for direct array response
        return {
            data: Array.isArray(response.data) ? response.data : [],
            meta: undefined,
            isParent: false // Data comes from branches endpoint
        };
    } catch (err: any) {
        // If it's a 400 error, try to get parent organization
        if (err?.response?.status === 400) {
            try {
                let parentResponse;
                if (organizationType === 'school') {
                    parentResponse = await getSchoolParent(organizationId);
                } else {
                    parentResponse = await getCompanyParent(organizationId);
                }
                // If parent exists, return it as an array with one element
                if (parentResponse.data) {
                    return {
                        data: [parentResponse.data],
                        meta: parentResponse.meta,
                        isParent: true // Data comes from parent endpoint
                    };
                }
            } catch (parentErr) {
                console.error(`Error fetching ${organizationType} parent:`, parentErr);
            }
        }
        // Re-throw the original error if it's not a 400 error or if parent fetch fails
        throw err;
    }
};

/**
 * Fetch personal user requests (partnership and attach requests)
 */
export const getPersonalUserRequests = async (
    userId: number
): Promise<{ partnerships: Partnership[]; attachRequests: any[] }> => {
    try {
        // Fetch partnership requests initiated by the user
        const partnershipsResponse = await apiClient.get(`/api/v1/users/${userId}/partnership_requests`);
        const partnerships = partnershipsResponse.data?.data || partnershipsResponse.data || [];

        // Fetch attach requests initiated by the user
        const attachResponse = await apiClient.get(`/api/v1/users/${userId}/attach_requests`);
        const attachRequests = attachResponse.data?.data || attachResponse.data || [];

        return {
            partnerships: Array.isArray(partnerships) ? partnerships : [],
            attachRequests: Array.isArray(attachRequests) ? attachRequests : []
        };
    } catch (err) {
        console.error('Error fetching personal user requests:', err);
        return {
            partnerships: [],
            attachRequests: []
        };
    }
};

/**
 * Fetch personal user organization requests (for teacher/user roles)
 * Uses /api/v1/users/me/organizations endpoint
 * Returns structure: { data: { schools: [...], companies: [...] }, meta: {...} }
 */
// Get membership requests (pending, accepted, rejected)
export const getUserMembershipRequests = async (): Promise<{ data: { schools: any[]; companies: any[] }; meta?: any }> => {
    const response = await apiClient.get('/api/v1/users/me/membership_requests');
    
    // Handle response structure: { data: { schools: [...], companies: [...] }, meta: {...} }
    return {
        data: response.data?.data || { schools: [], companies: [] },
        meta: response.data?.meta
    };
};

export const getPersonalUserOrganizations = async (): Promise<{ data: { schools: any[]; companies: any[] }; meta?: any }> => {
    const response = await apiClient.get('/api/v1/users/me/organizations');
    
    // Handle response structure: { data: { schools: [...], companies: [...] }, meta: {...} }
    if (response.data?.data) {
        return {
            data: {
                schools: response.data.data.schools || [],
                companies: response.data.data.companies || []
            },
            meta: response.data.meta
        };
    }
    
    // Fallback
    return {
        data: {
            schools: [],
            companies: []
        },
        meta: undefined
    };
};

/**
 * For personal users under 15: fetch companies that are partners of the user's confirmed schools.
 * Used in Personal settings → Organisations so minors can request membership only for those companies.
 * Returns 403 if the user is not a personal user or is not under 15.
 */
export const getJoinableCompaniesForMinor = async (params?: {
    search?: string;
    page?: number;
    per_page?: number;
}): Promise<{ data: Array<{ id: number; name: string; city?: string; zip_code?: string; logo_url?: string; full_name?: string; status?: string; email?: string; members_count?: number; company_type?: string }>; meta?: { current_page: number; total_pages: number; total_count: number; per_page: number } }> => {
    const response = await apiClient.get('/api/v1/users/me/joinable_companies', { params: params || {} });
    const data = response.data?.data ?? response.data ?? [];
    const list = Array.isArray(data) ? data : [];
    return {
        data: list,
        meta: response.data?.meta
    };
};

/**
 * Fetch all members from teacher's classes (students + volunteers)
 * Returns members from all classes the teacher manages
 */
export const getTeacherMembers = async (params?: {
    search?: string;
    member_type?: 'student' | 'volunteer';
    per_page?: number;
    page?: number;
}): Promise<OrganizationMember[]> => {
    const response = await apiClient.get('/api/v1/teachers/members', {
        params: {
            ...params,
            per_page: params?.per_page || 1000  // Load all members by default
        }
    });
    
    // Extract data array from paginated response
    // Backend returns: { data: [...], meta: {...} }
    const members = response.data?.data || response.data || [];
    
    // Map to OrganizationMember format (backend already provides compatible fields)
    // Preserve classes information for filtering by school
    return members.map((member: any) => ({
        id: member.id,
        first_name: member.first_name,
        last_name: member.last_name,
        full_name: member.full_name,
        email: member.email,
        role: member.role,
        avatar_url: member.avatar_url || null,
        classes: member.classes || [] // Preserve classes for school filtering
    }));
};

type EuMcProjectFields = {
    is_eu_mc_declared?: boolean;
    learning_outcomes?: string;
    participation_mode?: 'on_site' | 'online' | 'blended';
    workload_hours?: number | string;
    workload_ects?: number | string;
    project_eqf_level?: number;
    project_eqf_framework_type?: 'EQF' | 'QF_EHEA';
    assessment_type?: string;
    teaching_languages?: string[];
    entry_requirements?: string;
    validity_period_months?: number | null;
};

const appendEuMcProjectFieldsToFormData = (formData: FormData, project: EuMcProjectFields): void => {
    if (project.is_eu_mc_declared !== undefined) {
        formData.append('project[is_eu_mc_declared]', String(project.is_eu_mc_declared));
    }
    if (project.learning_outcomes) {
        formData.append('project[learning_outcomes]', project.learning_outcomes);
    }
    if (project.participation_mode) {
        formData.append('project[participation_mode]', project.participation_mode);
    }
    if (project.workload_hours !== undefined && project.workload_hours !== '') {
        formData.append('project[workload_hours]', String(project.workload_hours));
    }
    if (project.workload_ects !== undefined && project.workload_ects !== '') {
        formData.append('project[workload_ects]', String(project.workload_ects));
    }
    if (project.project_eqf_level != null) {
        formData.append('project[project_eqf_level]', String(project.project_eqf_level));
    }
    if (project.project_eqf_framework_type) {
        formData.append('project[project_eqf_framework_type]', project.project_eqf_framework_type);
    }
    if (project.assessment_type) {
        formData.append('project[assessment_type]', project.assessment_type);
    }
    if (project.entry_requirements) {
        formData.append('project[entry_requirements]', project.entry_requirements);
    }
    if (project.validity_period_months !== undefined) {
        formData.append(
            'project[validity_period_months]',
            project.validity_period_months == null ? '' : String(project.validity_period_months)
        );
    }
    if (project.teaching_languages && project.teaching_languages.length > 0) {
        project.teaching_languages.forEach((lang) => {
            formData.append('project[teaching_languages][]', lang);
        });
    }
};

/**
 * Create a new project (JSON format - without images)
 */
export const createProjectJSON = async (
    payload: CreateProjectPayload
): Promise<CreateProjectResponse> => {
    const response = await apiClient.post('/api/v1/projects', payload);
    return response.data;
};

/**
 * Create a new project (Multipart format - with images)
 */
export const createProjectMultipart = async (
    formData: FormData
): Promise<CreateProjectResponse> => {
    const response = await apiClient.post('/api/v1/projects', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/** Append MLDS nested attributes for multipart (création / mise à jour projet) */
export const appendMldsInformationToFormData = (
    formData: FormData,
    mlds: MLDSInformationAttributes,
    options?: { serviceQuoteFiles?: (File | null | undefined)[] }
): void => {
    const P = 'project[mlds_information_attributes]';

    if (mlds.id != null) {
        formData.append(`${P}[id]`, String(mlds.id));
    }

    if (mlds.requested_by) {
        formData.append(`${P}[requested_by]`, mlds.requested_by);
    }

    if (mlds.department_number !== undefined && mlds.department_number !== null) {
        formData.append(`${P}[department_number]`, String(mlds.department_number));
    }

    if (mlds.type_mlds) {
        formData.append(`${P}[type_mlds]`, mlds.type_mlds);
    }

    if (mlds.school_level_ids && mlds.school_level_ids.length > 0) {
        mlds.school_level_ids.forEach(id => {
            formData.append(`${P}[school_level_ids][]`, id.toString());
        });
    }

    if (mlds.target_audience) {
        formData.append(`${P}[target_audience]`, mlds.target_audience);
    }

    if (mlds.action_objectives && mlds.action_objectives.length > 0) {
        mlds.action_objectives.forEach(objective => {
            formData.append(`${P}[action_objectives][]`, objective);
        });
    }

    if (mlds.action_objectives_other !== undefined && mlds.action_objectives_other !== null) {
        formData.append(`${P}[action_objectives_other]`, mlds.action_objectives_other);
    }

    if (mlds.competencies_developed !== undefined && mlds.competencies_developed !== null) {
        formData.append(`${P}[competencies_developed]`, mlds.competencies_developed);
    }

    if (mlds.expected_participants !== undefined && mlds.expected_participants !== null) {
        formData.append(`${P}[expected_participants]`, mlds.expected_participants.toString());
    }

    if (mlds.financial_hse_lines !== undefined && mlds.financial_hse_lines !== null && Array.isArray(mlds.financial_hse_lines)) {
        mlds.financial_hse_lines.forEach((line: FinancialHseLine, index: number) => {
            formData.append(`${P}[financial_hse_lines][${index}][hse_name]`, line.hse_name);
            formData.append(`${P}[financial_hse_lines][${index}][hour]`, line.hour);
        });
    }

    if (mlds.financial_rate !== undefined && mlds.financial_rate !== null) {
        formData.append(`${P}[financial_rate]`, mlds.financial_rate.toString());
    }

    if (mlds.financial_hv !== undefined && mlds.financial_hv !== null) {
        formData.append(`${P}[financial_hv]`, mlds.financial_hv.toString());
    }

    if (mlds.financial_hv_lines !== undefined && mlds.financial_hv_lines !== null && Array.isArray(mlds.financial_hv_lines)) {
        mlds.financial_hv_lines.forEach((line: FinancialHvLine, index: number) => {
            formData.append(`${P}[financial_hv_lines][${index}][teacher_name]`, line.teacher_name);
            formData.append(`${P}[financial_hv_lines][${index}][hour]`, line.hour);
        });
    }

    if (mlds.financial_transport !== undefined && mlds.financial_transport !== null && Array.isArray(mlds.financial_transport)) {
        mlds.financial_transport.forEach((line: { transport_name: string; price: string }, index: number) => {
            formData.append(`${P}[financial_transport][${index}][transport_name]`, line.transport_name);
            formData.append(`${P}[financial_transport][${index}][price]`, line.price);
        });
    }

    if (mlds.financial_operating !== undefined && mlds.financial_operating !== null && Array.isArray(mlds.financial_operating)) {
        mlds.financial_operating.forEach((line: { operating_name: string; price: string }, index: number) => {
            formData.append(`${P}[financial_operating][${index}][operating_name]`, line.operating_name);
            formData.append(`${P}[financial_operating][${index}][price]`, line.price);
        });
    }

    if (mlds.financial_autres_financements !== undefined && mlds.financial_autres_financements !== null && Array.isArray(mlds.financial_autres_financements)) {
        mlds.financial_autres_financements.forEach((line: { autres_name: string; price: string }, index: number) => {
            formData.append(`${P}[financial_autres_financements][${index}][autres_name]`, line.autres_name);
            formData.append(`${P}[financial_autres_financements][${index}][price]`, line.price);
        });
    }

    if (
        mlds.mlds_financial_service_lines_attributes !== undefined &&
        mlds.mlds_financial_service_lines_attributes !== null &&
        Array.isArray(mlds.mlds_financial_service_lines_attributes)
    ) {
        const L = `${P}[mlds_financial_service_lines_attributes]`;
        let activeQuoteIndex = 0;
        mlds.mlds_financial_service_lines_attributes.forEach((line: MldsFinancialServiceLineAttribute, index: number) => {
            const isDestroy =
                line._destroy != null && line._destroy !== false && line._destroy !== '';
            if (isDestroy) {
                if (line.id != null) {
                    formData.append(`${L}[${index}][id]`, String(line.id));
                }
                formData.append(`${L}[${index}][_destroy]`, '1');
                return;
            }
            if (line.id != null) {
                formData.append(`${L}[${index}][id]`, String(line.id));
            }
            if (line.position !== undefined) {
                formData.append(`${L}[${index}][position]`, String(line.position));
            }
            formData.append(`${L}[${index}][service_name]`, line.service_name ?? '');
            formData.append(`${L}[${index}][price]`, line.price ?? '');
            if (line.hours !== undefined && line.hours !== null && String(line.hours).trim() !== '') {
                formData.append(`${L}[${index}][hours]`, String(line.hours));
            }
            if (line.comment !== undefined && line.comment !== null) {
                formData.append(`${L}[${index}][comment]`, line.comment);
            }
            const qf = options?.serviceQuoteFiles?.[activeQuoteIndex];
            activeQuoteIndex += 1;
            if (qf) {
                formData.append(`${L}[${index}][quote]`, qf);
            }
        });
    }

    if (mlds.objectives !== undefined && mlds.objectives !== null) {
        formData.append(`${P}[objectives]`, mlds.objectives);
    }
    if (mlds.network_issue_addressed !== undefined && mlds.network_issue_addressed !== null) {
        formData.append(`${P}[network_issue_addressed]`, mlds.network_issue_addressed);
    }
};

/** Nested member contexts for multipart (Rails) */
const appendMemberContextsToFormData = (
    formData: FormData,
    baseKey: 'project[co_responsible_contexts]' | 'project[participant_contexts]',
    contexts: MemberContextPayload[] | undefined
): void => {
    if (!contexts || contexts.length === 0) return;
    contexts.forEach((ctx, index) => {
        formData.append(`${baseKey}[${index}][user_id]`, String(ctx.user_id));
        formData.append(`${baseKey}[${index}][member_context_type]`, ctx.member_context_type);
        formData.append(`${baseKey}[${index}][member_context_id]`, String(ctx.member_context_id));
    });
};

export interface CreateProjectMultipartOptions {
    serviceQuoteFiles?: (File | null | undefined)[];
}

/**
 * Main function to create a project
 * Automatically chooses JSON or Multipart based on presence of images
 */
export const createProject = async (
    payload: CreateProjectPayload,
    mainImage?: File | null,
    additionalImages?: File[],
    documents?: File[],
    multipartOptions?: CreateProjectMultipartOptions
): Promise<CreateProjectResponse> => {
    // If no images, use JSON format
    const hasDocuments = !!documents && documents.length > 0;
    const hasServiceQuotes = multipartOptions?.serviceQuoteFiles?.some(f => f != null) ?? false;
    if (!mainImage && (!additionalImages || additionalImages.length === 0) && !hasDocuments && !hasServiceQuotes) {
        return createProjectJSON(payload);
    }

    // If images present, use multipart format
    const formData = new FormData();

    // Add context and organization_id
    formData.append('context', payload.context);
    if (payload.organization_id) {
        formData.append('organization_id', payload.organization_id.toString());
    }

    // Add project fields
    const project = payload.project;
    if (project.title) {
        formData.append('project[title]', project.title);
    }
    formData.append('project[description]', project.description);
    formData.append('project[start_date]', project.start_date);
    formData.append('project[end_date]', project.end_date);
    formData.append('project[status]', project.status);
    formData.append('project[private]', project.private.toString());

    if (project.project_kind) {
        formData.append('project[project_kind]', project.project_kind);
    }
    appendEuMcProjectFieldsToFormData(formData, project);

    // Add optional fields
    if (project.participants_number !== undefined) {
        formData.append('project[participants_number]', project.participants_number.toString());
    }

    if (project.school_level_ids && project.school_level_ids.length > 0) {
        project.school_level_ids.forEach(id => {
            formData.append('project[school_level_ids][]', id.toString());
        });
    }

    if (project.tag_ids && project.tag_ids.length > 0) {
        project.tag_ids.forEach(id => {
            formData.append('project[tag_ids][]', id.toString());
        });
    }

    if (project.skill_ids && project.skill_ids.length > 0) {
        project.skill_ids.forEach(id => {
            formData.append('project[skill_ids][]', id.toString());
        });
    }

    if (project.company_ids && project.company_ids.length > 0) {
        project.company_ids.forEach(id => {
            formData.append('project[company_ids][]', id.toString());
        });
    }

    if (project.keyword_ids && project.keyword_ids.length > 0) {
        project.keyword_ids.forEach(keyword => {
            formData.append('project[keyword_ids][]', keyword);
        });
    }

    if (project.group_ids && project.group_ids.length > 0) {
        project.group_ids.forEach(id => {
            formData.append('project[group_ids][]', id.toString());
        });
    }

    if (project.partnership_ids && project.partnership_ids.length > 0) {
        project.partnership_ids.forEach(id => {
            formData.append('project[partnership_ids][]', id.toString());
        });
    }

    // Add project members (Rails nested attributes format for multipart/form-data)
    if (project.project_members_attributes && project.project_members_attributes.length > 0) {
        project.project_members_attributes.forEach((member, index) => {
            formData.append(`project[project_members_attributes][${index}][user_id]`, member.user_id.toString());
            formData.append(`project[project_members_attributes][${index}][role]`, member.role);
            formData.append(`project[project_members_attributes][${index}][status]`, member.status);
        });
    }

    // Add links (Rails nested attributes format for multipart/form-data)
    if (project.links_attributes && project.links_attributes.length > 0) {
        project.links_attributes.forEach((link, index) => {
            formData.append(`project[links_attributes][${index}][name]`, link.name);
            formData.append(`project[links_attributes][${index}][url]`, link.url);
        });
    }

    // Add co_responsible_ids
    if (project.co_responsible_ids && project.co_responsible_ids.length > 0) {
        project.co_responsible_ids.forEach(id => {
            formData.append('project[co_responsible_ids][]', id.toString());
        });
    }

    // Add participant_ids
    if (project.participant_ids && project.participant_ids.length > 0) {
        project.participant_ids.forEach(id => {
            formData.append('project[participant_ids][]', id.toString());
        });
    }

    appendMemberContextsToFormData(formData, 'project[co_responsible_contexts]', project.co_responsible_contexts);
    appendMemberContextsToFormData(formData, 'project[participant_contexts]', project.participant_contexts);

    // Add MLDS information attributes (Rails nested attributes format)
    if (project.mlds_information_attributes) {
        appendMldsInformationToFormData(formData, project.mlds_information_attributes, {
            serviceQuoteFiles: multipartOptions?.serviceQuoteFiles
        });
    }

    // Add images
    if (mainImage) {
        formData.append('project[main_picture]', mainImage);
    }

    if (additionalImages && additionalImages.length > 0) {
        additionalImages.forEach(image => {
            formData.append('project[pictures][]', image);
        });
    }

    // Add documents (any file type)
    if (documents && documents.length > 0) {
        documents.forEach(file => {
            formData.append('project[documents][]', file);
        });
    }

    return createProjectMultipart(formData);
};

// ========================================
// Project documents
// ========================================

export interface ProjectDocument {
    id: number;
    filename: string;
    content_type: string | null;
    byte_size: number;
    created_at: string;
    url: string;
    visibility?: 'public' | 'private';
}

export const getProjectDocuments = async (projectId: number): Promise<{ data: ProjectDocument[] }> => {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/documents`);
    return response.data;
};

export const addProjectDocuments = async (
    projectId: number,
    files: File[],
    visibility: 'public' | 'private' = 'private'
): Promise<{ data: ProjectDocument[] }> => {
    const formData = new FormData();
    files.forEach(file => {
        formData.append('project[documents][]', file);
    });
    formData.append('visibility', visibility);

    const response = await apiClient.post(`/api/v1/projects/${projectId}/documents`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const updateProjectDocumentVisibility = async (
    projectId: number,
    attachmentId: number,
    visibility: 'public' | 'private'
): Promise<{ data: ProjectDocument[] }> => {
    const response = await apiClient.patch(
        `/api/v1/projects/${projectId}/documents/${attachmentId}`,
        { visibility }
    );
    return response.data;
};

export const deleteProjectDocument = async (
    projectId: number,
    attachmentId: number
): Promise<{ data: ProjectDocument[] }> => {
    const response = await apiClient.delete(`/api/v1/projects/${projectId}/documents/${attachmentId}`);
    return response.data;
};

export interface UpdateProjectPayload {
    organization_id?: number;
    project: {
        title?: string;
        description?: string;
        start_date?: string;
        end_date?: string;
        participants_number?: number;
        status?: 'draft' | 'to_process' | 'pending_validation' | 'coming' | 'in_progress' | 'ended' | 'archived';
        private?: boolean;
        school_level_ids?: number[];
        skill_ids?: number[];
        group_ids?: number[];
        tag_ids?: number[];
        company_ids?: number[];
        keyword_ids?: string[];
        links_attributes?: LinkAttribute[];
        co_responsible_ids?: number[];
        co_responsible_contexts?: MemberContextPayload[];
        participant_ids?: number[];
        participant_contexts?: MemberContextPayload[];
        partnership_ids?: number[];
        mlds_information_attributes?: MLDSInformationAttributes;
        learning_outcomes?: string;
        participation_mode?: 'on_site' | 'online' | 'blended';
        is_eu_mc_declared?: boolean;
        workload_hours?: number | string;
        workload_ects?: number | string;
        project_eqf_level?: number;
        project_eqf_framework_type?: 'EQF' | 'QF_EHEA';
        assessment_type?: string;
        teaching_languages?: string[];
        entry_requirements?: string;
        validity_period_months?: number | null;
    };
}

export interface UpdateProjectMultipartOptions {
    serviceQuoteFiles?: (File | null | undefined)[];
}

/** Champs projet (hors MLDS) pour FormData PATCH */
const appendUpdateProjectFieldsToFormData = (
    formData: FormData,
    project: UpdateProjectPayload['project']
): void => {
    if (project.title) formData.append('project[title]', project.title);
    if (project.description) formData.append('project[description]', project.description);
    if (project.start_date) formData.append('project[start_date]', project.start_date);
    if (project.end_date) formData.append('project[end_date]', project.end_date);
    if (project.status) formData.append('project[status]', project.status);
    if (project.private !== undefined) formData.append('project[private]', project.private.toString());

    appendEuMcProjectFieldsToFormData(formData, project);

    if (project.participants_number !== undefined) {
        formData.append('project[participants_number]', project.participants_number.toString());
    }

    if (project.tag_ids && project.tag_ids.length > 0) {
        project.tag_ids.forEach(id => {
            formData.append('project[tag_ids][]', id.toString());
        });
    }

    if (project.skill_ids && project.skill_ids.length > 0) {
        project.skill_ids.forEach(id => {
            formData.append('project[skill_ids][]', id.toString());
        });
    }

    if (project.company_ids && project.company_ids.length > 0) {
        project.company_ids.forEach(id => {
            formData.append('project[company_ids][]', id.toString());
        });
    }

    if (project.school_level_ids) {
        project.school_level_ids.forEach(id => {
            formData.append('project[school_level_ids][]', id.toString());
        });
    }

    if (project.keyword_ids && project.keyword_ids.length > 0) {
        project.keyword_ids.forEach(keyword => {
            formData.append('project[keyword_ids][]', keyword);
        });
    }

    if (project.group_ids && project.group_ids.length > 0) {
        project.group_ids.forEach(id => {
            formData.append('project[group_ids][]', id.toString());
        });
    }

    if (project.partnership_ids && project.partnership_ids.length > 0) {
        project.partnership_ids.forEach(id => {
            formData.append('project[partnership_ids][]', id.toString());
        });
    }

    if (project.co_responsible_ids && project.co_responsible_ids.length > 0) {
        project.co_responsible_ids.forEach(id => {
            formData.append('project[co_responsible_ids][]', id.toString());
        });
    }

    if (project.participant_ids && project.participant_ids.length > 0) {
        project.participant_ids.forEach(id => {
            formData.append('project[participant_ids][]', id.toString());
        });
    }

    appendMemberContextsToFormData(formData, 'project[co_responsible_contexts]', project.co_responsible_contexts);
    appendMemberContextsToFormData(formData, 'project[participant_contexts]', project.participant_contexts);

    if (project.links_attributes && project.links_attributes.length > 0) {
        project.links_attributes.forEach((link, index) => {
            formData.append(`project[links_attributes][${index}][name]`, link.name);
            formData.append(`project[links_attributes][${index}][url]`, link.url);
        });
    }
};

const updateProjectJSON = async (
    projectId: number,
    payload: UpdateProjectPayload
): Promise<CreateProjectResponse> => {
    const response = await apiClient.patch(`/api/v1/projects/${projectId}`, payload);
    return response.data;
};

const updateProjectMultipart = async (
    projectId: number,
    formData: FormData
): Promise<CreateProjectResponse> => {
    const response = await apiClient.patch(
        `/api/v1/projects/${projectId}`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
    );
    return response.data;
};

export const updateProject = async (
    projectId: number,
    payload: UpdateProjectPayload,
    mainImage?: File | null,
    additionalImages?: File[],
    multipartOptions?: UpdateProjectMultipartOptions
): Promise<CreateProjectResponse> => {
    const hasQuoteFiles = multipartOptions?.serviceQuoteFiles?.some(f => f != null) ?? false;
    // If no images and no devis prestataires, use JSON format
    if (!mainImage && (!additionalImages || additionalImages.length === 0) && !hasQuoteFiles) {
        return updateProjectJSON(projectId, payload);
    }

    const formData = new FormData();
    if (payload.organization_id != null) {
        formData.append('organization_id', payload.organization_id.toString());
    }
    appendUpdateProjectFieldsToFormData(formData, payload.project);
    if (payload.project.mlds_information_attributes) {
        appendMldsInformationToFormData(formData, payload.project.mlds_information_attributes, {
            serviceQuoteFiles: multipartOptions?.serviceQuoteFiles
        });
    }

    if (mainImage) {
        formData.append('project[main_picture]', mainImage);
    }

    if (additionalImages && additionalImages.length > 0) {
        additionalImages.forEach(image => {
            formData.append('project[pictures][]', image);
        });
    }

    return updateProjectMultipart(projectId, formData);
};

/**
 * Project Statistics Interface
 */
export interface ProjectStats {
    overview: {
        total_members: number;
        confirmed_members: number;
        pending_members: number;
        total_teams: number;
        total_badges_assigned: number;
    };
    members_by_role: {
        member: number;
        admin: number;
        co_owner: number;
        owner: number;
    };
    members_by_status: {
        pending: number;
        confirmed: number;
    };
    badges: {
        total: number;
        this_month: number;
        recent: Array<{
            id: number;
            badge_name: string;
            receiver_name: string;
            assigned_at: string;
        }>;
    };
    teams: {
        total: number;
        total_members: number;
    };
}

/**
 * Get project statistics
 */
export const getProjectStats = async (projectId: number): Promise<ProjectStats> => {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/stats`);
    return response.data;
};

/**
 * Submit MLDS bilan when closing a project (POST /api/v1/projects/:id/mlds_bilan)
 */
export const postMldsBilan = async (projectId: number, payload: MldsBilanPayload): Promise<void> => {
    await apiClient.post(`/api/v1/projects/${projectId}/mlds_bilan`, { mlds_bilan: payload });
};

/**
 * Explicit manual project closure (POST /api/v1/projects/:id/close)
 */
export const closeProject = async (projectId: number): Promise<any> => {
    const response = await apiClient.post(`/api/v1/projects/${projectId}/close`);
    return response.data;
};

export interface ProjectFunder {
    id: number;
    name: string;
    email: string;
    share_mode: 'nominatif' | 'anonyme';
    initials: string;
    has_linked_account: boolean;
    started_notified_at: string | null;
    closed_notified_at: string | null;
    confirmation_status?: 'pending' | 'confirmed' | 'declined';
    designation_kind?: 'email_only' | 'punctual' | 'attached';
    needs_confirmation?: boolean;
    company_name?: string | null;
}

export const getProjectFunders = async (projectId: number): Promise<ProjectFunder[]> => {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/funders`);
    return response.data?.data || [];
};

export const addProjectFunder = async (
    projectId: number,
    payload: { name: string; email: string; share_mode: 'nominatif' | 'anonyme' }
): Promise<ProjectFunder> => {
    const response = await apiClient.post(`/api/v1/projects/${projectId}/funders`, { funder: payload });
    return response.data;
};

export const removeProjectFunder = async (projectId: number, funderId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/projects/${projectId}/funders/${funderId}`);
};

export interface ProjectFunderFollow {
    token: string;
    kind: 'project';
    closed: boolean;
    declined?: boolean;
    declined_on?: string | null;
    closed_on?: string | null;
    title?: string;
    org?: string | null;
    org_kind?: string | null;
    date_range?: string;
    status_label?: string;
    description?: string | null;
    learning_outcomes?: string | null;
    share_mode?: 'nominatif' | 'anonyme';
    follow_active: boolean;
    needs_confirmation?: boolean;
    designation_kind?: string;
    confirmation_status?: string;
    funder_name?: string;
    funder_email?: string | null;
    funder_user_id?: number | null;
    funder_company_id?: number | null;
    viewer_is_funder?: boolean | null;
    financement?: string;
    informed_on?: string | null;
    report_due?: string | null;
    week_current?: number;
    week_total?: number;
    participants_count?: number;
    proofs_count?: number;
    last_activity_days?: number;
    signals?: Array<{ kind: string; label: string; detail: string }>;
    partners?: Array<{ name?: string; role?: string }>;
    designated_on?: string | null;
    report?: {
        weeks: number;
        participants_count: number;
        proofs_count: number;
        proof_project: boolean;
        life: string[];
        participants: Array<{ name: string; role: string; proofs: number }>;
        anonymous: boolean;
        transmitted_on?: string | null;
    };
}

export const getProjectFunderFollow = async (token: string): Promise<ProjectFunderFollow> => {
    const response = await apiClient.get(`/api/v1/projects/funder_follow/${token}`);
    return response.data;
};

export const confirmFunderFollowToken = async (token: string): Promise<ProjectFunderFollow> => {
    const response = await apiClient.post(`/api/v1/projects/funder_follow/${token}/confirm`);
    return response.data;
};

export const declineFunderFollowToken = async (token: string): Promise<ProjectFunderFollow> => {
    const response = await apiClient.post(`/api/v1/projects/funder_follow/${token}/decline`);
    return response.data;
};

export interface FundedProjectCard {
    token: string;
    title: string;
    org?: string | null;
    date_range: string;
    status: 'coming' | 'in_progress' | 'ended';
    status_label: string;
    closed_on?: string | null;
    report_transmitted: boolean;
    watch: boolean;
    watch_label?: string | null;
    closed_year?: number | null;
    needs_confirmation?: boolean;
    designation_kind?: string;
    confirmation_status?: string;
    funder_name?: string | null;
}

export const getFundedProjects = async (organizationId?: number): Promise<FundedProjectCard[]> => {
    const response = await apiClient.get('/api/v1/funded_projects', {
        params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response.data?.data || [];
};

export const confirmFundedProject = async (token: string, organizationId?: number): Promise<FundedProjectCard> => {
    const response = await apiClient.post(`/api/v1/funded_projects/${token}/confirm`, undefined, {
        params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response.data?.data;
};

export const declineFundedProject = async (token: string, organizationId?: number): Promise<void> => {
    await apiClient.post(`/api/v1/funded_projects/${token}/decline`, undefined, {
        params: organizationId ? { organization_id: organizationId } : undefined,
    });
};

export const proposeFunderAttachment = async (token: string, organizationId?: number): Promise<void> => {
    await apiClient.post(`/api/v1/funded_projects/${token}/propose_attachment`, undefined, {
        params: organizationId ? { organization_id: organizationId } : undefined,
    });
};

export const lookupFunderOrganization = async (email: string): Promise<{ id: number; type: string; name: string; city?: string; email: string } | null> => {
    const response = await apiClient.get('/api/v1/funder_lookup', { params: { email } });
    return response.data?.data || null;
};

export interface FunderAttachment {
    id: number;
    status: 'pending' | 'confirmed' | 'rejected';
    created_at: string;
    confirmed_at?: string | null;
    rejected_at?: string | null;
    funder_company?: { id: number; type: string; name: string; city?: string };
    carrier?: { id: number; type: string; name: string; city?: string };
    project_title?: string | null;
    direction?: 'sent' | 'received';
}

export const getFunderAttachments = async (
    organizationId: number,
    organizationType: 'company' | 'school' = 'company'
): Promise<{ sent: FunderAttachment[]; received: FunderAttachment[]; funders_count: number; funded_structures_count: number }> => {
    const response = await apiClient.get('/api/v1/funder_attachments', {
        params: { organization_id: organizationId, organization_type: organizationType },
    });
    return {
        sent: response.data?.data?.sent || [],
        received: response.data?.data?.received || [],
        funders_count: response.data?.meta?.funders_count || 0,
        funded_structures_count: response.data?.meta?.funded_structures_count || 0,
    };
};

export const confirmFunderAttachment = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/funder_attachments/${id}/confirm`);
};

export const rejectFunderAttachment = async (id: number): Promise<void> => {
    await apiClient.post(`/api/v1/funder_attachments/${id}/reject`);
};

/**
 * Join a project (request to join)
 */
export const joinProject = async (projectId: number): Promise<{
    message: string;
    project_member?: any;
}> => {
    const response = await apiClient.post(`/api/v1/projects/${projectId}/join`);
    return response.data;
};

/**
 * Get project members (including pending requests)
 */
export const getProjectMembers = async (projectId: number): Promise<any[]> => {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/members`);
    return response.data?.data || [];
};

/**
 * Get pending project join requests
 */
export const getProjectPendingMembers = async (projectId: number): Promise<any[]> => {
    const allMembers = await getProjectMembers(projectId);
    // Filter members with status 'pending'
    return allMembers.filter((member: any) => member.status === 'pending');
};

/**
 * Update project member (accept/reject request, change role, etc.)
 */
export const updateProjectMember = async (
    projectId: number,
    userId: number,
    updates: {
        role?: 'member' | 'co_owner' | 'admin';
        status?: 'pending' | 'confirmed';
        can_assign_badges_in_project?: boolean;
    }
): Promise<any> => {
    const response = await apiClient.patch(
        `/api/v1/projects/${projectId}/members/${userId}`,
        { member: updates }
    );
    return response.data;
};

/**
 * Remove project member (reject request or remove member)
 */
export const removeProjectMember = async (
    projectId: number,
    userId: number
): Promise<void> => {
    await apiClient.delete(`/api/v1/projects/${projectId}/members/${userId}`);
};

/**
 * Add a member to the project
 */
export const addProjectMember = async (
    projectId: number,
    userId: number
): Promise<any> => {
    const response = await apiClient.post(
        `/api/v1/projects/${projectId}/members`,
        { user_id: userId }
    );
    return response.data;
};

/** Bulk-add members (Patrick « Ajouter les N »). */
export const addProjectMembersBulk = async (
    projectId: number,
    userIds: number[],
    role: 'member' | 'co_responsible' | 'co_owner' | 'admin' = 'member'
): Promise<{
    message: string;
    data: { added: any[]; skipped: any[]; errors: any[] };
}> => {
    const response = await apiClient.post(
        `/api/v1/projects/${projectId}/members/bulk`,
        { user_ids: userIds, role }
    );
    return response.data;
};

export const preRegisterProjectParticipant = async (
    projectId: number,
    payload: {
        first_name: string;
        last_name: string;
        birthday: string;
        email?: string;
        user_role?: string;
        organization_id?: number;
        organization_type?: 'school' | 'company';
    }
): Promise<{ data: any; matched_existing: boolean; message: string }> => {
    const response = await apiClient.post(`/api/v1/projects/${projectId}/pre_register`, payload);
    return response.data;
};

// Team types and interfaces
export interface TeamMember {
    id: number;
    user: {
        id: number;
        full_name: string;
        email: string;
        avatar_url?: string;
        job?: string;
    };
}

export interface Team {
    id: number;
    title: string;
    description: string;
    team_leader?: {
        id: number;
        full_name: string;
        email: string;
        avatar_url?: string;
        job?: string;
    };
    team_members: TeamMember[];
    members_count: number;
    created_at: string;
    updated_at: string;
}

export interface CreateTeamPayload {
    title: string;
    description: string;
    team_leader_id?: number;
    team_member_ids: number[];
}

/**
 * Get all teams for a project
 */
export const getProjectTeams = async (projectId: number): Promise<Team[]> => {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/teams`);
    return response.data || [];
};

/**
 * Create a new team
 */
export const createProjectTeam = async (
    projectId: number,
    payload: CreateTeamPayload
): Promise<Team> => {
    const response = await apiClient.post(`/api/v1/projects/${projectId}/teams`, {
        team: payload
    });
    return response.data;
};

/**
 * Update a team
 */
export const updateProjectTeam = async (
    projectId: number,
    teamId: number,
    payload: CreateTeamPayload
): Promise<Team> => {
    const response = await apiClient.patch(
        `/api/v1/projects/${projectId}/teams/${teamId}`,
        { team: payload }
    );
    return response.data;
};

/**
 * Delete a team
 */
export const deleteProjectTeam = async (
    projectId: number,
    teamId: number
): Promise<void> => {
    await apiClient.delete(`/api/v1/projects/${projectId}/teams/${teamId}`);
};

/**
 * Branch Request interfaces
 */
export interface BranchRequest {
    id: number;
    parent_school?: {
        id: number;
        name: string;
        [key: string]: any;
    };
    parent_company?: {
        id: number;
        name: string;
        [key: string]: any;
    };
    child_school?: {
        id: number;
        name: string;
        [key: string]: any;
    };
    child_company?: {
        id: number;
        name: string;
        [key: string]: any;
    };
    initiator: 'parent' | 'child';
    recipient: 'parent' | 'child';
    status: 'pending' | 'confirmed' | 'rejected';
    message: string;
    confirmed_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateBranchRequestPayload {
    parent_school_id?: number;
    parent_company_id?: number;
    message: string;
}

export interface CreateBranchRequestResponse {
    message: string;
    data: BranchRequest;
}

/**
 * Create a branch request (for schools)
 */
export const createSchoolBranchRequest = async (
    schoolId: number,
    payload: CreateBranchRequestPayload
): Promise<CreateBranchRequestResponse> => {
    const response = await apiClient.post(
        `/api/v1/schools/${schoolId}/branch_requests`,
        payload
    );
    return response.data;
};

/**
 * Create a branch request (for companies)
 */
export const createCompanyBranchRequest = async (
    companyId: number,
    payload: CreateBranchRequestPayload
): Promise<CreateBranchRequestResponse> => {
    const response = await apiClient.post(
        `/api/v1/companies/${companyId}/branch_requests`,
        payload
    );
    return response.data;
};

/**
 * Fetch branch requests for an organization
 */
export const getBranchRequests = async (
    organizationId: number,
    organizationType: 'school' | 'company'
): Promise<{ data: BranchRequest[]; meta?: any }> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/branch_requests`
        : `/api/v1/companies/${organizationId}/branch_requests`;

    const response = await apiClient.get(endpoint);
    
    // Handle response structure: { data: [...], meta: {...} }
    if (response.data?.data) {
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    }
    
    // Fallback for direct array response
    return {
        data: Array.isArray(response.data) ? response.data : [],
        meta: undefined
    };
};

/**
 * Confirm a branch request
 */
export const confirmBranchRequest = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    requestId: number
): Promise<void> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/branch_requests/${requestId}/confirm`
        : `/api/v1/companies/${organizationId}/branch_requests/${requestId}/confirm`;

    await apiClient.patch(endpoint);
};

/**
 * Reject a branch request
 */
export const rejectBranchRequest = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    requestId: number
): Promise<void> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/branch_requests/${requestId}/reject`
        : `/api/v1/companies/${organizationId}/branch_requests/${requestId}/reject`;

    await apiClient.patch(endpoint);
};

/**
 * Delete/Cancel a branch request (only by initiator)
 */
export const deleteBranchRequest = async (
    organizationId: number,
    organizationType: 'school' | 'company',
    requestId: number
): Promise<void> => {
    const endpoint = organizationType === 'school'
        ? `/api/v1/schools/${organizationId}/branch_requests/${requestId}`
        : `/api/v1/companies/${organizationId}/branch_requests/${requestId}`;

    await apiClient.delete(endpoint);
};
